import { useEffect, useMemo, useState, type FormEvent } from "react";

import { payWithTotp } from "@/api/pay_helpers";
import {
  useBuySteamTopup,
  useSteamCurrencyRates,
  useSteamGetAmount,
  useSteamTopupService,
} from "@/api/steam_topup";
import { useT } from "@/lib/i18n";
import { useToasts } from "@/store/toasts";

/**
 * Steam Top-up - port of the Django `product.html` branch for
 * `service_id == 1` (`base/static/scripts/steam_rub.js`).
 *
 *   ┌──────────────────┐   ┌──────────────────────────┐
 *   │  Steam Login     │   │ Currency │ Amount         │
 *   │  Amount (USD)    │   │ RUB      │ amt × rub_rate │
 *   │                  │   │ KZT      │ amt × kzt_rate │
 *   │  [BUY 13.6500]   │   │ UAH      │ amt × uah_rate │
 *   └──────────────────┘   └──────────────────────────┘
 *
 * Final USD = amount × markup-multiplier (read from
 * `/web/api/steam_topup/service`, same as Django's `product.price`).
 * Backend re-validates in /create_order so we don't have to be
 * authoritative on the client.
 */

// Fallback only. The authoritative login rule comes from the API
// (`login_validation_rule`), resolved server-side from the same DB
// field schema /create_order enforces — so the rule lives in one
// place (the DB). This constant is used solely when the API can't
// supply a rule (old backend, unresolved category) so the form
// still works. Accepts an email or a plain username.
const STEAM_LOGIN_FALLBACK_RE =
  /^([^@\s]+@[^@\s]+\.[^@\s]+|[a-zA-Z0-9_.+-]{1,64})$/;

// Compile a server-supplied bare regex (already JSON-unwrapped by the
// backend's _coerce_field_dict) into a RegExp. Null on empty/invalid
// so the caller can fall back to STEAM_LOGIN_FALLBACK_RE.
function compileRule(rule: string | null | undefined): RegExp | null {
  if (!rule) return null;
  try {
    return new RegExp(rule);
  } catch {
    return null;
  }
}

// Mirrors backend app/text_sanitize.strip_invisible: drop zero-width
// / control / format chars (\p{C} = Cc+Cf+Cs+Co+Cn) plus line/para
// separators, then trim ends. Customers paste logins with U+2063 &
// co. injected between glyphs; without scrubbing here the value
// fails the regex client-side even though the server cleans+accepts
// it. Normal spaces (\p{Zs}) are intentionally kept, same as backend.
const INVISIBLE_CHARS_RE = /[\p{C}\p{Zl}\p{Zp}]/gu;
function stripInvisible(s: string): string {
  return s.replace(INVISIBLE_CHARS_RE, "").trim();
}
const MIN_AMOUNT = 0.13; // matches the steam_topup template seed
const MAX_AMOUNT = 500;

export function SteamTopup() {
  const push = useToasts((s) => s.push);
  const t = useT();
  const service = useSteamTopupService();
  const rates = useSteamCurrencyRates();
  const buy = useBuySteamTopup();

  const [login, setLogin] = useState("");
  const [amountStr, setAmountStr] = useState("");
  // Web-only: amount can be entered in USD (default — same as the API)
  // or in a local currency that's converted to USD server-side. The
  // /api/v2 create flow is unchanged and stays USD-only.
  const [currency, setCurrency] = useState<"usd" | "rub" | "kzt" | "uah">(
    "usd",
  );
  const isUsd = currency === "usd";
  const region =
    currency === "rub" ? "ru" : currency === "kzt" ? "kz" : "ua";

  // Parsed input value, interpreted in `currency`.
  const localAmount = useMemo(() => {
    const n = parseFloat(amountStr);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [amountStr]);

  // Debounce so we don't hit the conversion endpoint every keystroke.
  const [debouncedLocal, setDebouncedLocal] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedLocal(localAmount), 400);
    return () => clearTimeout(id);
  }, [localAmount]);

  const conv = useSteamGetAmount({
    amount: debouncedLocal,
    region,
    enabled: !isUsd,
  });

  // USD value actually credited to the Steam wallet and sent to the
  // (unchanged) create_order flow.
  const usdAmount = isUsd ? localAmount : conv.data?.usd_price ?? 0;
  // True while the displayed USD doesn't yet reflect the current input
  // (debounce pending or request in flight) — block buy until fresh.
  const converting =
    !isUsd && (conv.isFetching || debouncedLocal !== localAmount);

  const markup = service.data?.price ?? 1;
  const finalUsd = usdAmount * markup;

  const loginRe = useMemo(
    () =>
      compileRule(service.data?.login_validation_rule) ??
      STEAM_LOGIN_FALLBACK_RE,
    [service.data?.login_validation_rule],
  );
  // Scrub invisible/zero-width chars (pasted U+2063 etc.) before both
  // validation and submit, mirroring the backend so a polluted-looking
  // login isn't rejected client-side.
  const cleanLogin = stripInvisible(login);
  const loginValid = loginRe.test(cleanLogin);
  const amountValid = usdAmount >= MIN_AMOUNT && usdAmount <= MAX_AMOUNT;
  const canBuy =
    !!service.data &&
    loginValid &&
    amountValid &&
    !converting &&
    !buy.isPending;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!service.data) return;
    if (!loginValid) {
      push({
        kind: "error",
        title: t("steam_topup.invalid_login"),
        body: t("steam_topup.invalid_login_body"),
      });
      return;
    }
    if (!amountValid) {
      push({
        kind: "error",
        title: t("steam_topup.invalid_amount"),
        body: `$${MIN_AMOUNT} - $${MAX_AMOUNT}`,
      });
      return;
    }
    try {
      const result = await payWithTotp(
        (code) =>
          buy.mutateAsync({
            service_id: service.data!.service_id,
            amount: usdAmount,
            login: cleanLogin,
            ...(code ? { totp_code: code } : {}),
          }),
        { title: "Confirm Steam top-up" },
      );
      if (result === null) return; // cancelled
      const isAsync = result.status === "in_progress";
      push({
        kind:
          result.status === "completed" || isAsync ? "success" : "info",
        title:
          result.status === "completed"
            ? t("steam_topup.completed")
            : isAsync
              ? t("steam_topup.in_progress")
              : result.status === "refunded"
                ? t("steam_topup.refunded")
                : t("steam_topup.insufficient"),
        link: {
          to: `/orders/${encodeURIComponent(result.custom_id)}`,
          label: t("steam_topup.view_order"),
        },
      });
      if (result.status !== "insufficient") {
        setAmountStr("");
      }
    } catch (err) {
      const message =
        err instanceof Error && "detail" in err
          ? String((err as { detail: unknown }).detail ?? err.message)
          : "Top-up failed";
      push({ kind: "error", title: "Top-up failed", body: message });
    }
  };

  // Side-table currency lines. Empty rate row → "-" cell.
  const rateOf = (ccy: string): number | null => {
    const r = rates.data?.rates?.[ccy];
    return typeof r === "number" && r > 0 ? r : null;
  };
  const showSide = (ccy: string) => {
    const r = rateOf(ccy);
    if (r === null) return "-";
    return (usdAmount * r).toFixed(2);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">{t("steam_topup.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("steam_topup.subtitle")}</p>
      </div>

      {service.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Steam top-up isn't configured for this account. Contact your
          manager.
        </div>
      )}

      <form
        onSubmit={submit}
        className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]"
      >
        {/* --- left: inputs + buy --- */}
        <section className="card space-y-4 p-4 sm:p-6">
          <div>
            <label className="label" htmlFor="steam_login">
              {t("steam_topup.login_label")}
            </label>
            <input
              id="steam_login"
              type="text"
              className="input"
              placeholder={t("steam_topup.login_placeholder")}
              autoComplete="off"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              required
            />
            {!loginValid && login.length > 0 && (
              <div className="mt-1 text-xs text-red-600">
                {t("steam_topup.invalid_login_body")}
              </div>
            )}
          </div>

          <div>
            <label className="label" htmlFor="amount">
              {t("steam_topup.amount_label")}
            </label>
            <div className="flex gap-2">
              <select
                aria-label="Currency"
                className="input w-28"
                value={currency}
                onChange={(e) =>
                  setCurrency(
                    e.target.value as "usd" | "rub" | "kzt" | "uah",
                  )
                }
              >
                <option value="usd">USD</option>
                <option value="rub">RUB</option>
                <option value="kzt">KZT</option>
                <option value="uah">UAH</option>
              </select>
              <input
                id="amount"
                type="number"
                inputMode="decimal"
                className="input flex-1"
                step="0.01"
                min={0}
                placeholder={isUsd ? "0.13" : "1000"}
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                required
              />
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {isUsd ? (
                <>
                  Min ${MIN_AMOUNT.toFixed(2)} · Max $
                  {MAX_AMOUNT.toFixed(0)}
                </>
              ) : converting ? (
                "Converting…"
              ) : conv.isError ? (
                <span className="text-amber-700">
                  Conversion unavailable for {currency.toUpperCase()} right
                  now.
                </span>
              ) : usdAmount > 0 ? (
                <>
                  ≈ ${usdAmount.toFixed(2)} credited · charged $
                  {finalUsd.toFixed(2)} · USD {MIN_AMOUNT.toFixed(2)}–
                  {MAX_AMOUNT.toFixed(0)}
                </>
              ) : (
                `Enter the ${currency.toUpperCase()} amount to credit`
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-surface-border pt-4 text-sm">
            <div>
              {converting ? (
                <span className="text-slate-400">Converting…</span>
              ) : usdAmount > 0 ? (
                <>
                  <span className="text-slate-500">You'll pay</span>{" "}
                  <span className="font-semibold text-slate-800 tabular-nums">
                    ${finalUsd.toFixed(4)}
                  </span>
                </>
              ) : (
                <span className="text-slate-400">
                  Enter an amount to see the total.
                </span>
              )}
            </div>
            <button
              type="submit"
              className="btn btn-primary text-sm tabular-nums"
              disabled={!canBuy}
            >
              {buy.isPending
                ? t("steam_topup.cta_busy")
                : usdAmount > 0 && !converting
                  ? `${t("steam_topup.cta")} · $${finalUsd.toFixed(4)}`
                  : t("steam_topup.cta")}
            </button>
          </div>
        </section>

        {/* --- right: currency conversion table --- */}
        <section className="card overflow-hidden">
          <div className="border-b border-surface-border bg-surface-muted px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
            Local equivalent
          </div>
          <table className="w-full text-sm">
            <tbody>
              {[
                { ccy: "rub", label: "RUB" },
                { ccy: "kzt", label: "KZT" },
                { ccy: "uah", label: "UAH" },
              ].map((row) => (
                <tr
                  key={row.ccy}
                  className="border-t border-surface-border first:border-t-0"
                >
                  <th
                    scope="row"
                    className="px-4 py-2.5 text-left font-medium text-slate-600"
                  >
                    {row.label}
                  </th>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-800">
                    {usdAmount > 0 ? showSide(row.ccy) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rates.isError && (
            <div className="border-t border-surface-border px-4 py-2 text-xs text-amber-700">
              Rates unavailable - totals are USD-only until rates load.
            </div>
          )}
        </section>
      </form>
    </div>
  );
}
